import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserState } from 'src/app/_shared/state/user.state';
import { ReportsService } from 'src/app/reports/reports.service';
import { ReportsState } from 'src/app/reports/reports.state';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import constants from 'src/constant.json';
import { CmaService } from '../cma.service';
import { CmaState } from '../cma.state';

@Component({
  selector: 'app-view-cma-report',
  templateUrl: './view-cma-report.component.html',
  styleUrls: ['./view-cma-report.component.scss'],
})
export class ViewCmaReportComponent implements OnInit {
  onDestroyNotifier$ = new Subject();
  upgradeSnackbar: MatSnackBarRef<any>;
  createdBy: any;
  isScreenWidthSmall: any;
  defaultProfileImage = constants.userAvatar;
  currentDiv: string = 'details';
  message: string;

  onScrollEvent = {
    'padding-top': '0px',
  };

  showSlider = false;
  cmaReportId: string;
  cmaViewDetails: any;
  userId: string;
  slideComponents = ['details', 'compare', 'valuation', 'cma-trends', 'more'];
  currentSlideIndex = 0;
  showFullScreenSlideShow = false;
  userRole: string;

  constructor(
    private router: Router,
    private cmaService: CmaService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private snackbar: MatSnackBar,
    private userState: UserState,
    public cmaState: CmaState,
    public reportsState: ReportsState,
    private subscriptionService: SubscriptionPlanService,
    private reportsService: ReportsService
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.activatedRoute.queryParams.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res && res.id) {
        this.cmaReportId = res.id;
        if (this.cmaReportId) {
          this.getReportDetails(this.cmaReportId);
        } else {
          this.openSnackbar('Report Id missing', false);
        }
        this.cdr.detectChanges();
      }
    });

    this.cmaState.cmaReports.pipe().subscribe((res: any) => {
      if (res) {
        this.cmaViewDetails = res;
        this.createdBy = res.createdUserInfo;
        if (this.cmaViewDetails?.reportCategory == 'smartReport') {
          this.slideComponents = ['details', 'compare', 'cma-trends', 'more'];
        } else {
          this.slideComponents = ['details', 'compare', 'valuation', 'cma-trends', 'more'];
        }
      }
    });

    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res) {
        this.userId = res.id;
        this.userRole = res.roles[0];
      }
    });
  }

  ngAfterViewInit() {
    if (window.history.state.from && window.history.state.from == 'comp') {
      this.scrollTo('compare');
    }
    this.cdr.detectChanges();
  }

  checkScreenSize() {
    this.isScreenWidthSmall = window.innerWidth <= 768; // set breakpoint for mobile
  }

  getReportDetails(cmaReportId) {
    this.cmaService.getCmaReportById(cmaReportId, 'view').subscribe({
      next: (response) => {
        if (response?.isLoading) {
          return;
        }

        if (response?.message === 'No reports found for this user.') {
          this.message = 'Report no longer exist';
        }
        if (response.error) {
          console.error('Error fetching report:', response.error);
        }
      },
      error: (err) => console.error('Unexpected error:', err),
      complete: () => console.log('Subscription completed.'),
    });
  }

  editCma() {
    if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.editCma.key)) return;

    let queryParams: any = {
      id: this.cmaReportId,
    };
    this.router.navigate(['/cma/edit'], { queryParams: queryParams });
  }

  back() {
    this.activatedRoute.queryParams.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res?.from == 'myProperty') {
        this.router.navigate(['/my-property']);
      } else if (res?.from == 'dashboard') {
        this.router.navigate(['/dashboard']);
      } else {
        this.router.navigate(['/cma']);
      }
    });
    this.cmaState.cmaReportsValue = null;
  }

  onMouseWheel(evt) {
    this.onVisible(document.querySelectorAll('.sectioninfo'));
  }

  onVisible(list) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          this.currentDiv = entry.target.id;
        } else {
          io.disconnect();
        }
      });
    });
    list.forEach((el) => {
      io.observe(el);
    });
  }

  scrollTo(id) {
    this.currentDiv = id;
    document.getElementById(id).scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.onScrollEvent['padding-top'] = '70px';
  }

  toggleSlider() {
    this.showSlider = !this.showSlider;
  }

  startSlideShow() {
    this.currentSlideIndex = 0;
    this.showFullScreenSlideShow = true;
  }

  nextSlide() {
    if (this.currentSlideIndex < this.slideComponents.length - 1) {
      this.currentSlideIndex++;
    }
  }

  prevSlide() {
    if (this.currentSlideIndex > 0) {
      this.currentSlideIndex--;
    }
  }

  exitSlideShow() {
    this.showFullScreenSlideShow = false;
  }

  openSnackbar(upgradeMsg, success = false) {
    this.upgradeSnackbar = this.snackbar.open(upgradeMsg, 'Dismiss', {
      duration: 5000,
      panelClass: [success ? 'snackbar-success' : 'snackbar-error'],
      verticalPosition: 'top',
    });
  }

  closeSliderFromChild() {
    this.showSlider = false; // Hide the slider
  }

  download() {
    let params = {
      cobranded: false,
      referenceId: this.cmaViewDetails.cmaReportId,
    };
    let name = this.cmaViewDetails.reportName;
    if (this.cmaViewDetails.reportCategory == 'smartReport') {
      name = name + ' ' + 'Smart research report';
    } else {
      name = name + ' ' + 'CMA report';
    }
    this.reportsService.downloadReport(params, name);
  }
}
